import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FlipCard } from '../components/FlipCard';
import { api } from '../api';
import type { Plant } from '../types';
import { CATEGORIES } from '../constants';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const LEARNED_KEY = 'pflanzenkarten_learned';

function loadLearned(): Set<string> {
  try {
    const raw = localStorage.getItem(LEARNED_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore */ }
  return new Set();
}

function saveLearned(ids: Set<string>) {
  localStorage.setItem(LEARNED_KEY, JSON.stringify([...ids]));
}

type LearnFilter = 'alle' | 'nicht-gemerkt' | 'gemerkt';

export const CardsPage: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [deck, setDeck] = useState<Plant[]>([]);
  const [index, setIndex] = useState(0);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('Alle');
  const [learnFilter, setLearnFilter] = useState<LearnFilter>('alle');
  const [learnedIds, setLearnedIds] = useState<Set<string>>(loadLearned);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getPlants()
      .then((data) => { setPlants(data); setDeck(data); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Build deck from category + learnFilter + shuffle
  const buildDeck = useCallback((
    allPlants: Plant[],
    cat: string,
    filter: LearnFilter,
    learned: Set<string>,
    doShuffle: boolean,
  ) => {
    let base = cat === 'Alle' ? allPlants : allPlants.filter((p) => p.category === cat);
    if (filter === 'gemerkt') base = base.filter((p) => learned.has(p.id));
    if (filter === 'nicht-gemerkt') base = base.filter((p) => !learned.has(p.id));
    return doShuffle ? shuffle(base) : base;
  }, []);

  const indexRef = React.useRef(index);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const prevDepsRef = React.useRef({ 
    activeCategory, 
    learnFilter, 
    shuffleMode, 
    plantsLength: plants.length 
  });

  // Re-build deck whenever dependencies change
  useEffect(() => {
    if (plants.length === 0) return;

    const depsChanged = 
      prevDepsRef.current.activeCategory !== activeCategory ||
      prevDepsRef.current.learnFilter !== learnFilter ||
      prevDepsRef.current.shuffleMode !== shuffleMode ||
      prevDepsRef.current.plantsLength !== plants.length;

    prevDepsRef.current = { 
      activeCategory, 
      learnFilter, 
      shuffleMode, 
      plantsLength: plants.length 
    };

    setDeck((prevDeck) => {
      const newDeck = buildDeck(plants, activeCategory, learnFilter, learnedIds, shuffleMode);
      const currentIdx = indexRef.current;

      if (depsChanged || newDeck.length === 0) {
        setIndex(0);
      } else {
        const currentPlant = prevDeck[currentIdx];
        if (currentPlant) {
          const newIdx = newDeck.findIndex((p) => p.id === currentPlant.id);
          if (newIdx !== -1) {
            setIndex(newIdx);
          } else {
            // The plant is no longer in the deck (e.g. was marked learned in 'nicht-gemerkt' mode)
            if (currentIdx >= newDeck.length) {
              setIndex(newDeck.length - 1);
            } else {
              setIndex(currentIdx);
            }
          }
        } else {
          setIndex(0);
        }
      }
      return newDeck;
    });
  }, [plants, activeCategory, learnFilter, learnedIds, shuffleMode, buildDeck]);

  const selectCategory = useCallback((cat: string) => {
    setActiveCategory(cat);
    setIndex(0);
  }, []);

  const setFilter = useCallback((f: LearnFilter) => {
    setLearnFilter(f);
    setIndex(0);
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffleMode((prev) => !prev);
    setIndex(0);
  }, []);

  const toggleLearned = useCallback((plantId: string) => {
    setLearnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(plantId)) next.delete(plantId);
      else next.add(plantId);
      saveLearned(next);
      return next;
    });
  }, []);

  const goNext = useCallback(() => setIndex((i) => (i + 1) % Math.max(deck.length, 1)), [deck.length]);
  const goPrev = useCallback(() => setIndex((i) => (i - 1 + Math.max(deck.length, 1)) % Math.max(deck.length, 1)), [deck.length]);

  // Stats
  const learnedCount = learnedIds.size;
  const totalCount = plants.length;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5">
        <div className="w-14 h-14 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20 animate-pulse">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
            <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
          </svg>
        </div>
        <p className="text-creamDim text-sm font-mono">Karten werden geladen…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <div className="panel p-8 max-w-md text-center flex flex-col gap-4 shadow-cardHover">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-red-500">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <p className="plant-name text-xl text-cream font-semibold">Verbindungsfehler</p>
          <p className="text-creamDim text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary mx-auto">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (plants.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <div className="panel p-10 max-w-sm text-center flex flex-col items-center gap-5 shadow-cardHover">
          <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" className="text-sage/60">
              <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
            </svg>
          </div>
          <div>
            <h1 className="plant-name text-2xl font-bold text-cream">Noch keine Pflanzen</h1>
            <p className="text-creamDim text-sm mt-1">Füge deine ersten Pflanzenkarten im Adminbereich hinzu.</p>
          </div>
          <Link to="/admin" className="btn-primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Pflanze hinzufügen
          </Link>
        </div>
      </div>
    );
  }

  // Filter tabItems to only show categories with at least 1 plant
  const counts: Record<string, number> = { Alle: plants.length };
  for (const cat of CATEGORIES) {
    counts[cat] = plants.filter((p) => p.category === cat).length;
  }
  const tabItems = ['Alle', ...CATEGORIES.filter((cat) => counts[cat] > 0)];

  const filterOptions: { key: LearnFilter; label: string; icon: string }[] = [
    { key: 'alle', label: 'Alle', icon: '📚' },
    { key: 'nicht-gemerkt', label: 'Nicht gemerkt', icon: '🔄' },
    { key: 'gemerkt', label: 'Gemerkt', icon: '✓' },
  ];

  return (
    <>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-10 nav-glass">
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-sage/15 flex items-center justify-center border border-sage/20">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
              </svg>
            </div>
            <span className="plant-name text-cream font-semibold text-lg">Pflanzenkarten</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Progress indicator */}
            {totalCount > 0 && (
              <span className="text-xs font-mono text-sage bg-sage/10 border border-sage/20 px-2.5 py-1 rounded-full">
                {learnedCount}/{totalCount} gemerkt
              </span>
            )}
            <Link to="/learn" className="btn-ghost text-xs">
              Lernen
            </Link>
            <Link to="/admin" className="btn-ghost text-xs">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
              Admin
            </Link>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1.5 px-5 pb-2 overflow-x-auto scrollbar-none">
          {tabItems.map((cat) => {
            const count = cat === 'Alle' ? plants.length : plants.filter((p) => p.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => selectCategory(cat)}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200
                  ${activeCategory === cat
                    ? 'bg-sage text-white border-sage'
                    : 'bg-white/70 text-creamDim border-border/60 hover:border-sage/50 hover:text-sage'}`}
              >
                {cat}
                <span className={`text-[10px] px-1 rounded-full font-mono
                  ${activeCategory === cat ? 'bg-white/20' : 'bg-border/40'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Learn filter row */}
        <div className="flex gap-1.5 px-5 pb-2.5 overflow-x-auto scrollbar-none">
          {filterOptions.map(({ key, label, icon }) => {
            const catBase = activeCategory === 'Alle' ? plants : plants.filter((p) => p.category === activeCategory);
            const cnt = key === 'alle'
              ? catBase.length
              : key === 'gemerkt'
              ? catBase.filter((p) => learnedIds.has(p.id)).length
              : catBase.filter((p) => !learnedIds.has(p.id)).length;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200
                  ${learnFilter === key
                    ? key === 'gemerkt'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : key === 'nicht-gemerkt'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-sage text-white border-sage'
                    : 'bg-white/70 text-creamDim border-border/60 hover:border-sage/50 hover:text-sage'}`}
              >
                <span>{icon}</span>
                {label}
                <span className={`text-[10px] px-1 rounded-full font-mono
                  ${learnFilter === key ? 'bg-white/20' : 'bg-border/40'}`}>
                  {cnt}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <main className="pt-32">
        {deck.length === 0 ? (
          <div className="min-h-[80vh] flex flex-col items-center justify-center gap-4 p-6">
            <div className="panel p-10 max-w-sm text-center flex flex-col items-center gap-4 shadow-cardHover">
              <div className="w-14 h-14 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20 text-2xl">
                {learnFilter === 'gemerkt' ? '🎉' : learnFilter === 'nicht-gemerkt' ? '🔄' : '🌿'}
              </div>
              <p className="plant-name text-xl text-cream">
                {learnFilter === 'gemerkt'
                  ? 'Alles gemerkt!'
                  : learnFilter === 'nicht-gemerkt'
                  ? 'Keine ungemerkten Karten'
                  : 'Keine Karten in dieser Kategorie'}
              </p>
              <p className="text-creamDim text-sm">
                {learnFilter === 'gemerkt'
                  ? 'Du hast alle Karten dieser Kategorie gelernt. Super!'
                  : learnFilter === 'nicht-gemerkt'
                  ? 'Alle Karten wurden bereits als gemerkt markiert.'
                  : 'Wechsle die Kategorie oder füge Einträge im Admin-Bereich hinzu.'}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setFilter('alle')} className="btn-ghost">Alle anzeigen</button>
                {learnFilter !== 'alle' && (
                  <button onClick={() => selectCategory('Alle')} className="btn-ghost">Alle Kategorien</button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <FlipCard
            plant={deck[index]}
            index={index}
            total={deck.length}
            onPrev={goPrev}
            onNext={goNext}
            shuffleMode={shuffleMode}
            onToggleShuffle={toggleShuffle}
            isLearned={learnedIds.has(deck[index]?.id ?? '')}
            onToggleLearned={() => toggleLearned(deck[index].id)}
          />
        )}
      </main>
    </>
  );
};
